import React, { useCallback, useEffect, useState } from "react";

const VERDICT_LABEL = {
  pass: "Pass",
  warn: "Warn",
  block: "Block",
  check_error: "Check error",
};

export function VerdictDot({ verdict, size = 12 }) {
  const color =
    verdict === "block" ? "#5a5a5a" :
    verdict === "check_error" ? "#7a7a7a" :
    verdict === "warn" ? "#b0b0b0" : "#ededed";
  return (
    <span
      className="verdict-dot"
      style={{
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: "50%",
        background: color,
        marginRight: 6,
        verticalAlign: "middle",
      }}
      aria-label={`verdict: ${verdict}`}
    />
  );
}

/**
 * ComplianceDrawer — the right-hand "Policy Checker" panel on the IO review
 * page. It is the single home for a single IO's compliance detail: the
 * Lawspeaker verdict, score, policy DB version, per-platform verdicts with
 * their collapsible issue lists, and the override controls.
 *
 * The body renders the same <LawspeakerPanel> the IO page used to show in its
 * bottom grid, so the panel's styling and information are identical by
 * construction rather than duplicated. The drawer header carries the verdict
 * chip, IO number and close control.
 *
 * On a wide viewport an open drawer is laid out as a real third column (see
 * the `:has(.compliance-drawer-open)` rules in styles.css) rather than an
 * overlay, so it never covers the columns it sits beside.
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   ioNumber?: string,
 *   title?: string,
 *   verdict?: string,
 *   error?: string | null,
 *   panel: React.ReactNode,
 *   actions?: React.ReactNode,
 * }} props
 */

export default function ComplianceDrawer({
  open,
  onClose,
  ioNumber,
  title,
  verdict,
  error,
  panel,
  actions,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const cls = verdict === "pass" ? "pass" : verdict === "block" ? "block" : verdict === "check_error" ? "check_error" : "warn";

  return (
    <aside
      className={`compliance-drawer${open ? " compliance-drawer-open" : ""}`}
      aria-hidden={!open}
      aria-label="Compliance warnings and fixes"
    >
      <div className="compliance-drawer-header">
        <div>
          {verdict && (
            <span className={`verdict-label verdict-label-${cls}`}>
              {VERDICT_LABEL[verdict] || verdict}
            </span>
          )}
          {ioNumber && <h3 className="compliance-drawer-title mono">{ioNumber}</h3>}
          {title && <p className="compliance-drawer-sub">{title}</p>}
        </div>
        <button className="btn-icon compliance-drawer-close" onClick={onClose} aria-label="Close warnings panel">
          ✕
        </button>
      </div>

      <div className="compliance-drawer-body">
        {error && !panel ? (
          <div className="compliance-error">
            <strong>Compliance check could not complete</strong>
            <p>{error}</p>
            <p className="compliance-hint">
              Re-run checks once the compliance service is available. Approval and
              generation still require manual compliance confirmation in the meantime.
            </p>
          </div>
        ) : (
          panel
        )}

        {actions && <div className="compliance-drawer-actions">{actions}</div>}
      </div>
    </aside>
  );
}

/**
 * The open/close toggle for a ComplianceDrawer: a narrow vertical tab on the
 * right edge labelled "Policy Checker", reading top-to-bottom. Kept separate
 * so the drawer and its toggle can be placed independently within a page.
 */
export function ComplianceDrawerToggle({ open, onClick, disabled, title }) {
  return (
    <button
      type="button"
      className={`compliance-drawer-toggle${open ? " compliance-drawer-toggle-open" : ""}`}
      onClick={onClick}
      disabled={disabled}
      aria-label={open ? "Close compliance warnings" : "Open compliance warnings"}
      title={title || (open ? "Close warnings" : "Show warnings and what to fix")}
    >
      <span className="compliance-drawer-toggle-label">Policy Checker</span>
    </button>
  );
}

/**
 * Convenience hook: manages open state plus the 20px toggle for a page.
 * Returns { open, openDrawer, closeDrawer, toggle, Toggle } so a page can
 * render the toggle wherever it fits.
 */
export function useComplianceDrawer() {
  const [open, setOpen] = useState(false);
  const openDrawer = useCallback(() => setOpen(true), []);
  const closeDrawer = useCallback(() => setOpen(false), []);
  const toggle = useCallback(() => setOpen((o) => !o), []);
  return { open, openDrawer, closeDrawer, toggle };
}
